import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from "@angular/common/http";
import "rxjs/add/operator/toPromise";
import "rxjs/add/observable/of";
import { environment } from "../../../environments/environment";
import { Observable } from "rxjs/Observable";
import { catchError, tap } from "rxjs/operators";
import { ErrorObservable } from "rxjs/observable/ErrorObservable";
import { Outlet } from "./outlet.model";
import { CognitoService } from "../../services/cognito/cognito.service";
import { AwsSignerService } from "../../services/aws-signer/aws-signer.service";
import { Encoder } from "../../utils/encoder";
import { OutletListResponse } from "./outlet-list-reponse.model";

@Injectable()
export class OutletService {
  hasJustRegistered: boolean;
  hasJustUpdated: boolean;

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
    private awsSignerService: AwsSignerService
  ) {
    this.hasJustRegistered = false;
    this.hasJustUpdated = false;
  }

  private getConfigUrl() {
    return environment.configBasePath;
  }
  
  getRegionList(): Observable<any> {
    return this.http.get<Array<any>>(this.getConfigUrl() + "/management-region.json")
      .pipe(catchError(err => this.handleError(err)));
  }

  private getUrl() {
    return `${localStorage.getItem("region-api")}/v1/datamart/manage/outlet`;
  }

  getOutletList(): Observable<any> {
    let url = this.getUrl();

    const awsHeaders = this.awsSignerService.getAwsHeader(url, "GET");

    const headerParams = {
      "Content-Type": "application/json",
      Authorization: awsHeaders["Authorization"],
      "X-Amz-Date": awsHeaders["X-Amz-Date"],
      "x-amz-security-token": awsHeaders["x-amz-security-token"],
      "x-api-key": environment.cesAPI.apiKey,
    };

    if (this.hasJustRegistered) {
      // headerParams['Cache-Control'] = 'max-age=0';
    }

    const httpOptions = {
      headers: new HttpHeaders(headerParams),
    };

    return this.http
      .get<OutletListResponse>(url, httpOptions)
      .pipe(catchError((err) => this.handleError(err)));
  }

  private setOperator(outlet: Outlet,operation: string){
    const currentUser = this.cognitoService.getUserIdentity();
    if(currentUser && currentUser.length > 0){
      outlet = {
        [operation]: currentUser,
        ...outlet
      }
    }
    return outlet;
  }
  
  saveOutlet(outlet: Outlet): Observable<any> {

    const saveOutlet = this.setOperator(outlet,"createBy");
    const awsHeaders = this.awsSignerService.getAwsHeader(this.getUrl(), 'POST', saveOutlet);

    const headerParams = {
      'Content-Type': 'application/json',
      'Authorization': awsHeaders['Authorization'],
      'X-Amz-Date': awsHeaders['X-Amz-Date'],
      'x-amz-security-token': awsHeaders['x-amz-security-token'],
      'x-api-key': environment.cesAPI.apiKey,
    };

    const httpOptions = {
      headers: new HttpHeaders(headerParams)
    };

    return this.http.post<Outlet>(this.getUrl(), saveOutlet, httpOptions)
      .pipe(
        tap(() => this.hasJustRegistered = true),
        catchError(err => this.handleError(err))
      );
  }
  updateOutlet(outlet: Outlet): Observable<any> {
    const urlUpdate = `${this.getUrl()}/${Encoder.encode(outlet.id)}`;
    const updateOutlet = this.setOperator(outlet, "modifiedBy");
    const awsHeaders = this.awsSignerService.getAwsHeader(
      urlUpdate,
      "PUT",
      updateOutlet
    );

    const headerParams = {
      "Content-Type": "application/json",
      Authorization: awsHeaders["Authorization"],
      "X-Amz-Date": awsHeaders["X-Amz-Date"],
      "x-amz-security-token": awsHeaders["x-amz-security-token"],
      "x-api-key": environment.cesAPI.apiKey,
    };

    if (this.hasJustRegistered) {
      // headerParams['Cache-Control'] = 'max-age=0';
    }

    const httpOptions = {
      headers: new HttpHeaders(headerParams),
    };

    return this.http.put<Outlet>(urlUpdate, updateOutlet, httpOptions).pipe(
      tap(() => (this.hasJustUpdated = true)),
      catchError((err) => this.handleError(err))
    );
  }

  getOutletById(outletId): Observable<any> {
    const encodedId = Encoder.encode(outletId);
    const awsHeaders = this.awsSignerService.getAwsHeader(
      `${this.getUrl()}/${encodedId}`,
      "GET"
    );

    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Authorization: awsHeaders["Authorization"],
        "X-Amz-Date": awsHeaders["X-Amz-Date"],
        "x-amz-security-token": awsHeaders["x-amz-security-token"],
        "x-api-key": environment.cesAPI.apiKey,
      }),
    };

    return this.http
      .get<Outlet>(`${this.getUrl()}/${encodedId}`, httpOptions)
      .pipe(catchError((err) => this.handleError(err)));
  }

  uploadImage(fileFormData: any): Observable<any> {
    const awsHeaders = this.awsSignerService.getAwsHeader(this.getUploadImageUrl(), 'POST', fileFormData);

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': awsHeaders['Authorization'],
        'X-Amz-Date': awsHeaders['X-Amz-Date'],
        'x-amz-security-token': awsHeaders['x-amz-security-token'],
        'x-api-key': environment.cesAPI.apiKey,
      })
    };

    return this.http.post<any>(this.getUploadImageUrl(), fileFormData, httpOptions)
      .pipe(catchError(err => this.handleError(err))
      );
  }

  private getUploadImageUrl() {
    return `${localStorage.getItem('region-api')}/v1/datamart/manage/imgUpload/outlets`;
  }

  private handleError(error: HttpErrorResponse) {
    if (
      error.error.error &&
      error.error.error.message ===
        "The security token included in the request is expired"
    ) {
      this.cognitoService.logout();
      return;
    }
    if (error.error.error && error.error.error.message) {
      return new ErrorObservable(error.error.error.message);
    }
    if (error.error.error instanceof ErrorEvent) {
      // A provider-side or network error occurred. Handle it accordingly.
      console.error("An error occurred:", error.error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(
        `Backend returned code ${error.error.status}, ` + `body was: ${error.error.error}`
      );
    }
    // return an ErrorObservable with a user-facing error message
    return new ErrorObservable(
      "Something bad happened. Please try again later."
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/outlet/outlet.service.ts